import { useState } from 'react';
import PropTypes from 'prop-types';

import { connect } from 'react-redux';

import reduce from 'lodash/fp/reduce';

import { legacyTheme, styled } from '@prose-ui/legacy';

import { BasicModal } from 'Components/BasicModal';
import Card from 'Components/Card';
import { Button } from 'Components/LegacyButton';
import Spacer from 'Components/Spacer';
import { Typography } from 'Components/Typography';

import CardProps from 'PropTypes/CardProps';
import consultationCategoryProp from 'PropTypes/consultationProps';

import { goals as content } from 'assets/content/consultation/consultation';
import { ReactComponent as QuestionMarkSvg } from 'assets/images/active-question.svg';

import * as consultationSelectors from 'dux/consultation/selectors';

import QuestionLayout from './QuestionLayout';

const QuestionMarkButton = styled.button`
  display: inline-block;
  appearance: none;
  border: none;
  background-color: transparent;
  width: 14px;
  height: 14px;
  padding: 0px;
  cursor: pointer;
`;

const LightPurpleModal = styled(BasicModal)`
  display: flex;
  flex-direction: column;
  align-items: center;
  text-align: center;
`;

const SupplementDislaimerModal = styled(BasicModal)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  backgroundPosition: 'center',
  backgroundSize: 'cover',
  backgroundImage: `url(${content.hairSupplementsDisclaimerModal.imageBackground.desktop.src})`,
  [legacyTheme.breakpoints.match('sm')]: {
    backgroundImage: `url(${content.hairSupplementsDisclaimerModal.imageBackground.mobile.src})`,
  },
});

const CardArea = styled.div`
  ${({ row1 = null, row2 = null }) => `
    display: flex;
    flex-wrap: wrap;
    justify-content: center;
    align-content: flex-start;
    margin: 15px 0;

    ${legacyTheme.breakpoints.match('sm')} {
        max-width: 100%;
    }

    ${
      row1
        ? `
        display: grid;
        grid-template: "1fr";

        ${legacyTheme.breakpoints.match('md')} {
              flex: 1 0 auto;
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          align-content: flex-start;
          margin: 15px 0;
        }
        `
        : ''
    }
    ${
      row2
        ? `
        max-width: 750px;

        ${legacyTheme.breakpoints.match('sm')} {
            max-width: 100%;
        }
        `
        : ''
    }
  `}
`;

const SupplementDislaimerText = styled.div`
  flex: 1;
  margin-top: 10px;
  max-width: 366px;
`;

const DescriptionSupplements = styled(Typography)`
  margin-bottom: 1.5em;
`;

const SupplementsDislaimer = styled(Typography)`
  margin-bottom: '0.75em';
`;

const HairSupplementsDisclaimerModalButton = styled(Button)`
  margin-bottom: '30px';
`;

export const Goals = ({
  cardProps,
  consultationCategory,
  cta,
  initialValue,
  isSaving,
  onNext,
  options,
  questionIndex,
  title,
}) => {
  const [isModalOpen, setModalOpen] = useState(false);
  const [isSupplementsDisclaimerModalOpen, setSupplementsDisclaimerModalOpen] = useState(false);
  const [values, setValues] = useState(() =>
    reduce(
      (accumulator, option) => ({
        ...accumulator,
        [option.value]: initialValue?.[option.value],
      }),
      {},
      options
    )
  );
  const selectedOptions = options.filter(option => values[option.value]);
  const selectedList = selectedOptions.map(option => option.value);

  const onOptionClick = option => () => {
    const { value } = option;

    const o = {};

    o[value] = !values[value];

    setValues({ ...values, ...o });
  };

  const submit = () => onNext(values);

  const showAntiBrassNotice = options.some(opt => opt.value === 'antiBrass');

  return (
    <QuestionLayout
      buttonLabel={cta}
      consultationCategory={consultationCategory}
      isSaving={isSaving}
      multi
      questionIndex={questionIndex}
      submit={submit}
      title={title}
    >
      <CardArea
        data-testid="consultation-options-area"
        row1={options?.length <= 5 && !options.some(opt => opt.imageUrl)}
        row2={options?.length >= 6}
      >
        {options.map((option, index) => (
          <Card
            key={index}
            {...cardProps}
            description={option.description}
            imageUrl={option.imageUrl}
            label={option.label}
            onClick={onOptionClick(option)}
            selectable={!selectedList?.length > 0 || !selectedOptions.find(opt => opt.unique)}
            selected={values[option.value]}
            theme="consultation"
          />
        ))}
        {showAntiBrassNotice && (
          <>
            <Spacer axis="vertical" size={24} />
            <Typography align="center" color="grey" variant="p3">
              {content.antiBrassNotice}
              &nbsp;
              <QuestionMarkButton
                data-click="tooltip-antibrass"
                data-from="goal"
                data-testid="subscription-modal-opener"
                onClick={() => setModalOpen(true)}
                type="button"
              >
                <QuestionMarkSvg />
              </QuestionMarkButton>
            </Typography>
          </>
        )}
      </CardArea>
      <SupplementDislaimerText>
        <Typography align="center" color="grey" variant="p3">
          {content.hairSupplementsNotice}
          &nbsp;
          <QuestionMarkButton
            data-click="tooltip-supplements"
            data-from="goal"
            data-testid="supplements-disclaimer-modal-opener"
            onClick={() => setSupplementsDisclaimerModalOpen(true)}
            type="button"
          >
            <QuestionMarkSvg />
          </QuestionMarkButton>
        </Typography>
      </SupplementDislaimerText>

      <SupplementDislaimerModal
        isOpen={isSupplementsDisclaimerModalOpen}
        onClose={() => setSupplementsDisclaimerModalOpen(false)}
      >
        <Typography color="noir" paragraph variant="h1r">
          {content.hairSupplementsDisclaimerModal.title}
        </Typography>
        <DescriptionSupplements color="noir" variant="p2">
          {content.hairSupplementsDisclaimerModal.descriptionSupplements}
        </DescriptionSupplements>
        <Typography color="noir" variant="p2">
          <b>Capsule 1</b> helps naturally rebalance the scalp, and <b>capsule 2</b> stimulates the
          hair bulb to grow faster and hold tighter, meaning less shedding.
        </Typography>
        <HairSupplementsDisclaimerModalButton
          marginTop
          onClick={() => setSupplementsDisclaimerModalOpen(false)}
          variant="noir"
        >
          {content.hairSupplementsDisclaimerModal.buttonLabel}
        </HairSupplementsDisclaimerModalButton>
        <SupplementsDislaimer color="noir" variant="p3">
          {content.hairSupplementsDisclaimerModal.supplementsDisclaimer}
        </SupplementsDislaimer>
        <Typography color="noir" variant="p3">
          †{' '}
          <a
            href={content.hairSupplementsDisclaimerModal.studyPdf}
            rel="noopener noreferrer"
            target="_blank"
          >
            Ex Vivo studies
          </a>{' '}
          on millet extract.
        </Typography>
      </SupplementDislaimerModal>

      <LightPurpleModal isOpen={isModalOpen} onClose={() => setModalOpen(false)}>
        <img
          alt={content.antiBrassModal.imageAlt}
          src={content.antiBrassModal.image.src}
          width="140"
        />
        <Typography color="noir" paragraph variant="h1r">
          {content.antiBrassModal.headline}
        </Typography>
        <Typography color="grey" variant="p2">
          {content.antiBrassModal.description}
        </Typography>
        <Button marginTop onClick={() => setModalOpen(false)} variant="noir">
          {content.antiBrassModal.buttonLabel}
        </Button>
      </LightPurpleModal>
    </QuestionLayout>
  );
};

Goals.defaultProps = {
  isSaving: false,
  multi: false,
  cardProps: null,
};

Goals.propTypes = {
  cardProps: PropTypes.shape(CardProps),
  consultationCategory: consultationCategoryProp.isRequired,
  cta: PropTypes.string.isRequired,
  /**
   * initial value should correspond to the value of an option
   * if multi, initial value should be an array
   */
  initialValue: PropTypes.shape({
    curl: PropTypes.bool.isRequired,
    shine: PropTypes.bool.isRequired,
    straight: PropTypes.bool.isRequired,
    volume: PropTypes.bool.isRequired,
    moisture: PropTypes.bool.isRequired,
    antiBrass: PropTypes.bool.isRequired,
    hairGrowth: PropTypes.bool.isRequired,
    hairShedding: PropTypes.bool.isRequired,
  }).isRequired,
  /** multi defines if it is possible to select multiple answers */
  multi: PropTypes.bool,
  isSaving: PropTypes.bool,
  /**
   * onNext will be called with the selection
   * if multi, result will be an array
   */
  onNext: PropTypes.func.isRequired,
  options: PropTypes.arrayOf(
    PropTypes.shape({
      description: PropTypes.string,
      imageUrl: PropTypes.string,
      label: PropTypes.string.isRequired,
      unique: PropTypes.bool,
      value: PropTypes.oneOfType([PropTypes.bool, PropTypes.string, PropTypes.number]).isRequired,
    })
  ).isRequired,
  questionIndex: PropTypes.number.isRequired,
  title: PropTypes.string.isRequired,
};

export default connect(
  (state, { consultationCategory, consultationSubsetCategory, questionIndex, routeParams }) => ({
    ...consultationSelectors.getConsultationQuestionForIndex(state, {
      consultationCategory,
      consultationSubsetCategory,
      questionIndex,
      routeParams,
    }),
    options: consultationSelectors.getOptionsForQuestion(state, {
      consultationCategory,
      consultationSubsetCategory,
      questionIndex,
      routeParams,
    }),
  })
)(Goals);
